import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { RootState } from '.';
import { miniSerializeErrorOverride } from './serialize-error';
import { callUnpublishSite } from 'src/services/site/publish/publish.service';
import { cloneLiveWebsiteVersion, getSiteAttrForVersion, getWebsiteDetails } from 'src/services/site/sync/sync.service';
import { scheduleFileFetch, updateFileDownloadUrls, updateFileIds } from './file.store';
import { TSessionIdentifiersAndVersion, TSiteData } from 'src/types/publish.type';
import { createPageForTemplate } from 'src/helpers/page.helper';
import { TTemplate } from 'src/types/entity/template.type';
import { debounceManager } from 'src/hooks/useDebounced';
import { syncDraftAction } from './draft-sync.store';
import createPublishTask from '../Tasks/publishTask';
import taskQueue from '../core/task-queue';
import { CustomError } from 'src/lib/error';
import { eventBus } from 'src/core/event-bus';
import { UNPUBLISH_SUCCESSFUL } from 'src/helpers/errors.helper';
import imageManager from '../core/image-manager';
import { fromSiteState } from '../helpers/site-state.helper';
import logger from 'src/logger';
import { getLiveSiteDetailsAction, setLiveDataUnpublished, updateLiveSiteData } from './live-site.store';
import { getLatestKeyVersion, hasPublishedSite } from 'src/helpers/version.helper';
import { transformSiteDataToLiveFormat } from 'src/services/site/sync/sync.mapper';
import { DisplayInModalErrors, ErrorCodes } from 'src/constants/error-codes';
import { tellWebhook } from 'src/utils/webhook.util';
import { isProduction } from 'src/helpers/env.helper';
import { getSessionIdentifier } from 'src/helpers/session.helpers';

type TinitialState = Omit<TSiteData, 'page' | 'siteAttr'> & {
  isPublishUnpublishInProgress: boolean;
  siteAttr: Record<string, boolean> | null;
};

export const unpublishSiteAction = createAsyncThunk('siteUnpublish/post', async (_, { getState, dispatch }) => {
  const {
    session,
    siteData: { otherVersions },
  } = getState() as RootState;
  try {
    if (!session) throw new CustomError({ errorCode: 'SessionNotFound' });
    await callUnpublishSite({ ...getSessionIdentifier(session), domainName: session.domainName }, { otherVersions });
    dispatch(setLiveDataUnpublished()); // clear live site data from redux store after unpublish
    if (isProduction()) {
      tellWebhook(
        `❌ ${session.domainName}`,
        `❌ Site Unpublished: <a href="https://${session.domainName}?utm_source=flock-internal">${session.domainName}</a>`,
      );
    }
    return eventBus.emit('success/showToast', UNPUBLISH_SUCCESSFUL);
  } catch (err) {
    logger.reportError(err, { message: 'Error in unpublishSiteAction' });
    eventBus.emit('error/showToast', err);
    throw err;
  }
});

export const restoreLiveWebsiteVersion = createAsyncThunk(
  'siteDetails/post',
  async (
    {
      versionToClone,
    }: {
      versionToClone: number;
    },
    { getState, dispatch },
  ) => {
    const { session } = getState() as RootState;
    try {
      if (!session) throw new CustomError({ errorCode: 'SessionNotFound' });
      const resp = await cloneLiveWebsiteVersion({
        ...getSessionIdentifier(session),
        domainName: session.domainName,
        versionToClone: versionToClone,
      });
      const { fileDownloadUrls, fileIds, expiry, ...rest } = resp;

      fileDownloadUrls && dispatch(updateFileDownloadUrls(fileDownloadUrls));
      if (fileIds) {
        dispatch(updateFileIds(fileIds));
        if (expiry && fileIds.length > 0) {
          dispatch(scheduleFileFetch({ fileIds: fileIds, expiry: expiry }));
        }
      }
      return rest;
    } catch (err) {
      logger.reportError(err, { message: 'Error in restoreLiveWebsiteVersion' });
      eventBus.emit('error/showToast', err);
      throw err;
    }
  },
);

export const getSiteDetailsAction = createAsyncThunk(
  'siteDetails/get',
  async (_, { getState, dispatch }) => {
    const { session } = getState() as RootState;
    try {
      if (!session) throw new CustomError({ errorCode: 'SessionNotFound' });
      const resp = await getWebsiteDetails({ ...getSessionIdentifier(session), domainName: session.domainName });
      const { fileDownloadUrls, fileIds, expiry, ...rest } = resp;

      fileDownloadUrls && dispatch(updateFileDownloadUrls(fileDownloadUrls));
      if (fileIds) {
        dispatch(updateFileIds(fileIds));
        if (expiry && fileIds.length > 0) {
          dispatch(scheduleFileFetch({ fileIds: fileIds, expiry: expiry }));
        }
      }

      // dispatch an action to add live site data to redux store
      const { otherVersions, siteAttr, version } = resp || {};
      const { isPublished: isCurrentVersionPublished } = (siteAttr && fromSiteState(siteAttr)) || {};
      if (isCurrentVersionPublished && version) {
        // transform the response site data to the live format and update the live site data in the store
        // this does not trigger an API call as the data has already been fetched for current version
        dispatch(updateLiveSiteData(transformSiteDataToLiveFormat(resp)));
      } else {
        const havePublishedSite = siteAttr && otherVersions && hasPublishedSite(fromSiteState(siteAttr), otherVersions);
        if (havePublishedSite) {
          const publishedSiteVersion = otherVersions && getLatestKeyVersion(otherVersions);
          // If the latest version key exists, dispatch an action to get the live site details for the latest version
          // this triggers an API call to fetch the data for the latest version
          publishedSiteVersion && dispatch(getLiveSiteDetailsAction(publishedSiteVersion));
        } else {
          dispatch(setLiveDataUnpublished());
        }
      }

      return rest;
    } catch (err: any) {
      logger.reportError(err, { message: 'Error in getSiteDetailsAction' });
      if (err.error !== ErrorCodes.UnsupportedAppVersion) {
        eventBus.emit('error/showToast', err);
      }
      throw err;
    }
  },
  {
    serializeError: miniSerializeErrorOverride,
  },
);

export const handleWebsitePublish = createAsyncThunk(
  'sitePublish/post',
  async (template: TTemplate, { getState, dispatch }) => {
    const {
      draftData: { localChangesVersion: LCV },
      session,
      siteData: { siteAttr },
    } = getState() as RootState;
    try {
      if (!session) throw new CustomError({ errorCode: 'SessionNotFound' });

      if (imageManager.isFileUploadInProgress()) {
        throw new CustomError({ errorCode: 'UploadInProgress' });
      }
      const page = createPageForTemplate(template);

      if (LCV > 0 || siteAttr?.isDraft === false) {
        debounceManager.dispatchImmediate(syncDraftAction as any, 'sync', { template }, dispatch);
      }

      await new Promise((resolve, reject) => {
        const task = createPublishTask(
          { page, domainName: session.domainName, ...getSessionIdentifier(session) },
          {
            onSuccess: () => resolve(null as any),
            onFailure: ({ error }) => reject(error),
          },
        );
        taskQueue.enqueue(task);
      });
      dispatch(getLiveSiteDetailsAction());
    } catch (err: any) {
      logger.reportError(err, { message: 'Error in handleWebsitePublish' });
      if (err && err.error === ErrorCodes.WebsitePublishInProgress) {
        eventBus.emit(
          'error/showToast',
          new CustomError({
            errorCode: ErrorCodes.WebsitePublishInProgress,
            desc: DisplayInModalErrors[ErrorCodes.WebsitePublishInProgress].desc,
          }),
        );
      } else if (err && err.error !== ErrorCodes.RecordsNotVerified) {
        // Don't show any toast for RecordsNotVerified error since we have a modal for that, show toast for all other errors
        eventBus.emit('error/showToast', err);
      }
      throw err;
    }
  },
  {
    serializeError: miniSerializeErrorOverride,
  },
);

const initialState: TinitialState = {
  isPublishUnpublishInProgress: false,
  siteAttr: null,
};

const SitePublishSlice = createSlice({
  name: 'siteData',
  initialState,
  reducers: {
    setIsPublishUnpublishInProgress: (state, action) => {
      state.isPublishUnpublishInProgress = action.payload;
    },
    updateSiteData: (state, action) => {
      const { siteAttr, ...rest } = action.payload;
      const siteAttrApp = siteAttr ? fromSiteState(siteAttr) : state.siteAttr;
      const updates = { ...rest, siteAttr: siteAttrApp };
      return { ...state, ...updates };
    },
  },
  extraReducers: builder => {
    builder.addCase(getSiteDetailsAction.fulfilled, (state, action) => {
      const { page, siteAttr, ...rest } = action.payload;
      const siteAttrApp = siteAttr ? fromSiteState(siteAttr) : state.siteAttr;
      state = { ...state, ...rest, siteAttr: siteAttrApp };
      return state;
    });
    builder.addCase(restoreLiveWebsiteVersion.fulfilled, (state, action) => {
      const { page, siteAttr, ...rest } = action.payload;
      const siteAttrApp = siteAttr ? fromSiteState(siteAttr) : state.siteAttr;
      state = { ...state, ...rest, siteAttr: siteAttrApp };
      return state;
    });
    builder.addCase(handleWebsitePublish.pending, state => {
      state.isPublishUnpublishInProgress = true;
    });
    builder.addCase(handleWebsitePublish.rejected, state => {
      state.isPublishUnpublishInProgress = false;
    });
    builder.addCase(handleWebsitePublish.fulfilled, state => {
      state.isPublishUnpublishInProgress = false;
    });
    builder.addCase(unpublishSiteAction.pending, state => {
      state.isPublishUnpublishInProgress = true;
    });
    builder.addCase(unpublishSiteAction.rejected, state => {
      state.isPublishUnpublishInProgress = false;
    });
    builder.addCase(unpublishSiteAction.fulfilled, state => {
      state.isPublishUnpublishInProgress = false;
    });
  },
});

const { setIsPublishUnpublishInProgress, updateSiteData } = SitePublishSlice.actions;

export const publishUnpublishSelector = (state: RootState) => state.siteData.isPublishUnpublishInProgress;
export const siteAttrSelector = (state: RootState) => {
  return state.siteData.siteAttr;
};
export const siteDataSelector = (state: RootState) => {
  return state.siteData;
};
export { setIsPublishUnpublishInProgress, updateSiteData };

export default SitePublishSlice.reducer;

export const getSiteAttrThunk = createAsyncThunk(
  'get/siteAttr',
  async (_, { getState }) => {
    const { session, siteData } = getState() as RootState;
    try {
      if (!session) throw new CustomError({ errorCode: 'SessionNotFound' });
      const params: TSessionIdentifiersAndVersion = {
        ...getSessionIdentifier(session),
        version: siteData.version,
      };
      return await getSiteAttrForVersion(params);
    } catch (err) {
      logger.reportError(err, { message: 'Error in getSiteAttrThunk' });
      throw err;
    }
  },
  {
    serializeError: miniSerializeErrorOverride,
  },
);
